import React, { useState, useEffect } from 'react';
import { Layout, Menu, Button, theme, Dropdown, Space, Avatar, ConfigProvider } from 'antd';
import {
    MenuFoldOutlined,
    MenuUnfoldOutlined,
    DashboardOutlined,
    ShoppingCartOutlined,
    RetweetOutlined,
    ShopOutlined,
    SettingOutlined,
    UserOutlined,
    LogoutOutlined,
    TranslationOutlined,
    BgColorsOutlined,
    FileTextOutlined,
    BankOutlined,
    ImportOutlined,
    TeamOutlined
} from '@ant-design/icons';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import arEG from 'antd/locale/ar_EG';
import enUS from 'antd/locale/en_US';
import useBrandingStore from '../store/useBrandingStore';
import LiveClock from './LiveClock';

const { Header, Sider, Content } = Layout;

const MainLayout = () => {
    const [collapsed, setCollapsed] = useState(false);
    const [lang, setLang] = useState(localStorage.getItem('lang') || 'en');
    const [isDarkMode, setIsDarkMode] = useState(localStorage.getItem('theme') === 'dark');

    const { branding, fetchBranding } = useBrandingStore();

    const { defaultAlgorithm, darkAlgorithm } = theme;
    const {
        token: { colorBgContainer, borderRadiusLG },
    } = theme.useToken();

    const navigate = useNavigate();
    const location = useLocation();
    const user = JSON.parse(localStorage.getItem('user') || '{}');

    // Persist Language & Theme
    useEffect(() => {
        localStorage.setItem('lang', lang);
        document.body.dir = lang === 'ar' ? 'rtl' : 'ltr';
    }, [lang]);

    useEffect(() => {
        localStorage.setItem('theme', isDarkMode ? 'dark' : 'light');
    }, [isDarkMode]);

    useEffect(() => {
        fetchBranding();
    }, []);

    const handleMenuClick = (e) => {
        navigate(e.key);
    };

    const handleLogout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        navigate('/login');
    };

    const toggleLang = () => {
        setLang(prev => prev === 'en' ? 'ar' : 'en');
    };

    const toggleTheme = () => {
        setIsDarkMode(!isDarkMode);
    };

    const menuItems = [
        {
            key: '/',
            icon: <DashboardOutlined />,
            label: lang === 'ar' ? 'لوحة القيادة' : 'Dashboard',
        },
        {
            key: '/pos',
            icon: <ShoppingCartOutlined />,
            label: lang === 'ar' ? 'نقطة البيع' : 'POS',
        },
        {
            key: '/purchase-group',
            icon: <ImportOutlined />,
            label: lang === 'ar' ? 'المشتريات' : 'Purchasing',
            children: [
                {
                    key: '/purchase',
                    label: lang === 'ar' ? 'شراء منتجات' : 'Purchase Products',
                },
                {
                    key: '/purchase/supplier-invoices',
                    label: lang === 'ar' ? 'فواتير المشتريات' : 'Supplier Invoices',
                },
            ]
        },
        {
            key: '/suppliers',
            icon: <TeamOutlined />,
            label: lang === 'ar' ? 'الموردون' : 'Suppliers',
        },
        {
            key: '/invoices',
            icon: <FileTextOutlined />,
            label: lang === 'ar' ? 'الفواتير' : 'Invoices',
        },
        /* {
            key: '/buyback',
            icon: <RetweetOutlined />,
            label: lang === 'ar' ? 'شراء المستعمل' : 'Buyback',
        }, */
        {
            key: '/inventory-group',
            icon: <ShopOutlined />,
            label: lang === 'ar' ? 'المخزون' : 'Inventory',
            children: [
                {
                    key: '/inventory',
                    label: lang === 'ar' ? 'المخزون العام' : 'General Inventory',
                },
                /* {
                    key: '/inventory/used',
                    label: lang === 'ar' ? 'الأجهزة المستعملة' : 'Used Devices',
                } */
            ]
        },
        {
            key: '/accounting-group',
            icon: <BankOutlined />,
            label: lang === 'ar' ? 'المحاسبة' : 'Accounting',
            children: [
                {
                    key: '/accounting/dashboard',
                    label: lang === 'ar' ? 'لوحة القيادة' : 'Main Dashboard',
                },
                {
                    key: '/accounting/expenses',
                    label: lang === 'ar' ? 'المصاريف' : 'Expenses',
                },
                {
                    key: '/accounting/categories',
                    label: lang === 'ar' ? 'التصنيفات' : 'Categories',
                },
                {
                    key: '/accounting/reports',
                    label: lang === 'ar' ? 'التقارير المالية' : 'Financial Reports',
                },
            ]
        },

        {
            key: '/settings/company',
            icon: <SettingOutlined />,
            label: lang === 'ar' ? 'الإعدادات' : 'Settings',
        },
        {
            key: '/settings/users',
            icon: <UserOutlined />,
            label: lang === 'ar' ? 'المستخدمون' : 'Users',
        },
        {
            key: '/logs',
            icon: <FileTextOutlined />,
            label: lang === 'ar' ? 'السجلات' : 'Logs',
        },
    ];

    return (
        <ConfigProvider
            direction={lang === 'ar' ? 'rtl' : 'ltr'}
            locale={lang === 'ar' ? arEG : enUS}
            theme={{
                algorithm: isDarkMode ? darkAlgorithm : defaultAlgorithm,
            }}
        >
            <Layout style={{ minHeight: '100vh' }}>
                <Sider trigger={null} collapsible collapsed={collapsed} theme={isDarkMode ? 'dark' : 'light'}>
                    <div style={{ height: 64, display: 'flex', alignItems: 'center', justifyContent: 'center', borderBottom: isDarkMode ? '1px solid #303030' : '1px solid #f0f0f0', padding: '0 8px' }}>
                        {branding.logo_url ? (
                            <img
                                src={`/api${branding.logo_url}`}
                                alt="logo"
                                style={{ maxHeight: 40, maxWidth: '100%', objectFit: 'contain' }}
                            />
                        ) : (
                            <span style={{ fontSize: 18, fontWeight: 'bold', color: '#1890ff', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                                {collapsed ? 'POS' : branding.company_name}
                            </span>
                        )}
                        <div style={{ position: 'absolute', bottom: 10, left: 0, width: '100%', textAlign: 'center', fontSize: '10px', color: '#888', opacity: collapsed ? 0 : 0.6 }}>
                            POS Build: 2026-01-25 23:25
                        </div>
                    </div>
                    <Menu
                        theme={isDarkMode ? 'dark' : 'light'}
                        mode="inline"
                        defaultSelectedKeys={[location.pathname]}
                        items={menuItems}
                        onClick={handleMenuClick}
                    />
                </Sider>
                <Layout>
                    <Header style={{ padding: '0 16px', background: isDarkMode ? '#141414' : colorBgContainer, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: isDarkMode ? '1px solid #303030' : 'none' }}>
                        <Button
                            type="text"
                            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                            onClick={() => setCollapsed(!collapsed)}
                            style={{
                                fontSize: '16px',
                                width: 64,
                                height: 64,
                                color: isDarkMode ? '#fff' : undefined
                            }}
                        />

                        <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                            <LiveClock isDarkMode={isDarkMode} />
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <span style={{ color: isDarkMode ? '#fff' : '#595959', fontWeight: 500 }}>{user.name || user.username || 'Admin'}</span>
                                <Dropdown
                                    menu={{
                                        items: [
                                            {
                                                key: 'theme',
                                                label: isDarkMode ? 'Light Mode' : 'Dark Mode',
                                                icon: <BgColorsOutlined />,
                                                onClick: toggleTheme
                                            },
                                            {
                                                key: 'lang',
                                                label: lang === 'ar' ? 'English' : 'عربي',
                                                icon: <TranslationOutlined />,
                                                onClick: toggleLang
                                            },
                                            { type: 'divider' },
                                            {
                                                key: 'logout',
                                                label: lang === 'ar' ? 'تسجيل خروج' : 'Logout',
                                                icon: <LogoutOutlined />,
                                                onClick: handleLogout
                                            }
                                        ]
                                    }}
                                    trigger={['click']}
                                >
                                    <Avatar
                                        size="large"
                                        icon={<UserOutlined />}
                                        style={{ cursor: 'pointer', backgroundColor: '#1890ff' }}
                                    />
                                </Dropdown>
                            </div>
                        </div>
                    </Header>
                    <Content
                        style={{
                            margin: '24px 16px',
                            padding: 24,
                            minHeight: 280,
                            background: isDarkMode ? '#0a0a0a' : '#f0f2f5',
                            color: isDarkMode ? '#fff' : undefined
                        }}
                        data-theme={isDarkMode ? 'dark' : 'light'}
                    >
                        <div className="page-fade-in" style={{ height: '100%' }}>
                            <Outlet />
                        </div>
                    </Content>
                </Layout>
            </Layout>
        </ConfigProvider>
    );
};

export default MainLayout;
